import React, { Fragment, useEffect, useState } from "react";
import { AssetLocation, AssetLocationTypes, IReportQuery, Report, ReportParameter, ReportParameterDatePart, ReportParameterModifierTypes, ReportParameterTimeTypes, ReportParameterTypes } from "../../../../logic/api";
import { GridLegacy as Grid } from "@mui/material";

import TextInputField from "../../../../components/editable/EditableField/fields/TextInputField";
import DateField from "../../../../components/editable/EditableField/fields/DateField";
import TimeField from "../../../../components/editable/EditableField/fields/TimeField";
import NumberInputField from "../../../../components/editable/EditableField/fields/NumberInputField";
import LocationField from "../../../../components/editable/EditableField/fields/LocationField";
import FloatingInputLabel from "../../../../components/editable/EditableField/components/FloatingInputLabel";
import { addDays, addMonths, addYears, addHours, addMinutes, addSeconds, subDays } from "date-fns";
import { useDate } from "../../../../utility/useDate";
import DropDownField, { IDropDownListItem } from "../../../../components/editable/EditableField/fields/DropDownField";
import { useReleaseManagement, ReleaseFlag } from "../../../../utility/feature-management";
import { enumToSelectOptions } from "../../../../utility/utils";
import { useTranslation } from "react-i18next";

const ReportParamInput: React.FunctionComponent<{
    report: IReportQuery | Report;
    values: Record<string, unknown> | null;
    onParamsChange: (values: Record<string, unknown> | null, show: boolean) => void;
}> = (props) => {
    const { localizeDate } = useDate();
    const initialValues = Object.assign({}, props.values || {});
    const releaseRestrictHistoricalData = useReleaseManagement(ReleaseFlag.releaseRestrictHistoricalData);
    const { t } = useTranslation();

    for (const param of props.report.parameters) {
        if (param.name != null) {


            if (param.type == ReportParameterTypes.string) {
                if (initialValues[param.name] == null) {
                    initialValues[param.name] = param.defaultValue ?? "";
                }
            } else if (param.type == ReportParameterTypes.number) {
                if (initialValues[param.name] == null) {
                    initialValues[param.name] = param.defaultValue ?? "";
                }
            } else if (param.type == ReportParameterTypes.date || param.type == ReportParameterTypes.datetime) {

                let dateVal;

                if (param.defaultValueModifier == ReportParameterModifierTypes.dateOffset) {
                    // Calculate date offset based on the specified modifiers
                    // Get the current date
                    let d = new Date();

                    // Calculate date offset based on the specified modifiers
                    d = addDays(d, param.defaultOffsetDay ?? 0);
                    d = addMonths(d, param.defaultOffsetMonth ?? 0);
                    d = addYears(d, param.defaultOffsetYear ?? 0);
                    d = addHours(d, param.defaultOffsetHour ?? 0);
                    d = addMinutes(d, param.defaultOffsetMinute ?? 0);
                    d = addSeconds(d, param.defaultOffsetSecond ?? 0);

                    // Convert the resulting date to ISO string
                    dateVal = d.toISOString();
                } else if (param.defaultValueModifier == ReportParameterModifierTypes.lastDayOfLastCompleteWeek) {
                    // Get the date of the last complete week (starting from Monday and ending on Sunday)
                    const today = new Date();
                    const lastWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
                    const lastDayOfLastCompleteWeek = new Date(lastWeek.getFullYear(), lastWeek.getMonth(), lastWeek.getDate() + (6 - (lastWeek.getDay() === 0 ? 6 : lastWeek.getDay() - 1)));
                    dateVal = lastDayOfLastCompleteWeek.toISOString();

                } else if (param.defaultValueModifier == ReportParameterModifierTypes.lastDayOfLastCompletedCalendarMonth) {
                    // Get the date of the last complete month
                    const today = new Date();
                    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                    const lastDayOfLastCompletedCalendarMonth = new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0);
                    dateVal = lastDayOfLastCompletedCalendarMonth.toISOString();
                } else if (param.defaultValueModifier == ReportParameterModifierTypes.firstDayOfLastCompleteWeek) {

                    // Get the date of the first day of the last complete week (starting from Monday)
                    const today = new Date();
                    const lastWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
                    const firstDayOfLastCompleteWeek = new Date(lastWeek.getFullYear(), lastWeek.getMonth(), lastWeek.getDate() - (lastWeek.getDay() === 0 ? 6 : lastWeek.getDay() - 1));
                    dateVal = firstDayOfLastCompleteWeek.toISOString();
                } else if (param.defaultValueModifier == ReportParameterModifierTypes.firstDayOfLastCompletedCalendarMonth) {
                    // Get the date of the first day of the last complete month
                    const today = new Date();
                    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                    dateVal = new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 1).toISOString();
                } else {
                    // Legacy support for @u_startdate and @u_enddate start of day / end of day.
                    if ((param.name ?? "").toLowerCase() === "@u_startdate") {
                        const dt = localizeDate(new Date());
                        const startDate = dt.startOf('day').toISO();
                        dateVal = startDate;
                    } else if ((param.name ?? "").toLowerCase() === "@u_enddate") {
                        const dt = localizeDate(new Date());
                        const endDate = dt.endOf('day').toISO();
                        dateVal = endDate;
                    }
                }

                if (dateVal != null) {
                    if (param.defaultTimeType == ReportParameterTimeTypes.morning) {
                        try {
                            const dt = localizeDate(new Date(dateVal));
                            dateVal = dt.startOf('day').toISO();
                        } catch (e) {
                            console.warn(t("reports.failedDateMorning"), e);
                        }
                    } else if (param.defaultTimeType == ReportParameterTimeTypes.midnight) {
                        try {
                            const dt = localizeDate(new Date(dateVal));
                            dateVal = dt.endOf('day').toISO();
                        } catch (e) {
                            console.warn(t("reports.failedDateMidnight"), e);
                        }
                    }
                }

                if (initialValues[param.name] == null) {
                    initialValues[param.name] = dateVal;
                }

            } else if (param.type == ReportParameterTypes.datepart) {
                if (param.defaultValue) {
                    initialValues[param.name] = param.defaultValue;
                    continue;
                }

                if (!param.required) {
                    initialValues[param.name] = 'none';
                    continue;
                }

                initialValues[param.name] = 'day';
            }
        }
    }

    const [paramValues, setParamValues] = useState<Record<string, unknown>>(initialValues);

    // const [submittedParamValues, setSubmittedParamValues] = useState<Record<string, unknown> | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [errorId, setErrorId] = useState<number>(0);

    useEffect(() => {
        if (props.report.parameters.length == 0) {
            props.onParamsChange(paramValues, true);
        }
    }, [props.report.parameters]);


    const [timeoutCancel, setTimeoutCancel] = useState<NodeJS.Timeout | null>(null);
    const applyParameters = () => {
        if (timeoutCancel != null) {
            clearTimeout(timeoutCancel);
        }
        setTimeoutCancel(setTimeout(() => {
            setErrorMessage(null);
            for (const p of props.report.parameters) {
                if (p.required == true) {
                    const pVal = (paramValues[p.name] as string || "").toString();
                    if (pVal.length == 0) {
                        setErrorId(errorId + 1);
                        setErrorMessage(t("reports.aliasReq", { alias: p.alias }));
                        return;
                    }

                }
            }
            props.onParamsChange(paramValues, true);
        }, 1000));

    };

    const minDate = releaseRestrictHistoricalData ? subDays(new Date(new Date().setSeconds(0, 0)), 90) : undefined;

    useEffect(() => {
        applyParameters();
    }, [paramValues]);

    const locationField = (p: ReportParameter, value: string, displayTypes: AssetLocationTypes[], selectionTypes: AssetLocationTypes[]) => {
        return <LocationField
            displayTypes={displayTypes}
            selectionTypes={selectionTypes}
            label={p.alias}
            value={value} onChange={(val) => {
                const obj: Record<string, unknown> = {};
                const pValues = { ...paramValues };
                if (val == null) {
                    delete obj[p.name];
                    delete pValues[p.name];
                } else {
                    const assetLocation: AssetLocation = JSON.parse(val);
                    obj[p.name] = assetLocation.id;
                }

                setParamValues({
                    ...pValues,
                    ...obj
                });
            }}></LocationField>
    }

    return <Fragment>

        {props.report.parameters.length > 0 && (
            <div style={{ marginTop: 20 }}>
                <FloatingInputLabel label={t("reports.reportParams")} maxWidth={800}>
                    <div style={{ position: "relative", backgroundColor: "white", padding: 10, border: "solid 1px lightgray", borderRadius: 5, marginBottom: 20 }}>

                        <div style={{ marginTop: 20 }}>

                            <Grid container>
                                {props.report.parameters.map((p, i) => {
                                    const inputs: React.ReactNode[] = [];
                                    const value = paramValues[p.name] as string;
                                    if (p.type == ReportParameterTypes.number) {
                                        inputs.push(<NumberInputField label={p.alias} value={parseInt(value)} onChange={(val) => {
                                            const obj: Record<string, unknown> = {};

                                            obj[p.name] = val;

                                            setParamValues({
                                                ...paramValues,
                                                ...obj
                                            });
                                        }}></NumberInputField>)
                                    } else if (p.type == ReportParameterTypes.string) {
                                        inputs.push(<TextInputField label={p.alias} required={p.required} value={value} onChange={(val) => {
                                            const obj: Record<string, unknown> = {};

                                            obj[p.name] = val;

                                            setParamValues({
                                                ...paramValues,
                                                ...obj
                                            });
                                        }}></TextInputField>)
                                    } else if (p.type == ReportParameterTypes.date) {
                                        inputs.push(<DateField
                                            label={p.alias}
                                            value={value}
                                            minDate={minDate}
                                            onChange={(val) => {
                                                const obj: Record<string, unknown> = {};

                                                obj[p.name] = val;

                                                setParamValues({
                                                    ...paramValues,
                                                    ...obj
                                                });
                                            }}></DateField>)
                                    } else if (p.type == ReportParameterTypes.datetime) {
                                        inputs.push(<DateField
                                            label={p.alias}
                                            value={value}
                                            minDate={minDate}
                                            onChange={(val) => {
                                                const obj: Record<string, unknown> = {};

                                                obj[p.name] = val;

                                                setParamValues({
                                                    ...paramValues,
                                                    ...obj
                                                });
                                            }}></DateField>)
                                        inputs.push(<TimeField fiveMinutes={false} label={""} value={value} onChange={(val) => {
                                            const obj: Record<string, unknown> = {};

                                            obj[p.name] = val;

                                            setParamValues({
                                                ...paramValues,
                                                ...obj
                                            });
                                        }}></TimeField>)
                                    } else if (p.type == ReportParameterTypes.locationZone) {
                                        inputs.push(locationField(p, value, [AssetLocationTypes.Zone], [AssetLocationTypes.Zone]));
                                    } else if (p.type == ReportParameterTypes.locationLane) {
                                        inputs.push(locationField(p, value, [AssetLocationTypes.Zone, AssetLocationTypes.Lane], [AssetLocationTypes.Lane]));
                                    } else if (p.type == ReportParameterTypes.locationZoneOrLane) {
                                        inputs.push(locationField(p, value, [AssetLocationTypes.Zone, AssetLocationTypes.Lane], [AssetLocationTypes.Zone, AssetLocationTypes.Lane]));
                                    } else if (p.type == ReportParameterTypes.locationKeyroom) {
                                        inputs.push(locationField(p, value, [AssetLocationTypes.Keyroom], [AssetLocationTypes.Keyroom]));
                                    } else if (p.type == ReportParameterTypes.locationHook) {
                                        inputs.push(locationField(p, value, [AssetLocationTypes.Keyroom, AssetLocationTypes.Hook], [AssetLocationTypes.Hook]));
                                    } else if (p.type == ReportParameterTypes.locationKeyLocker) {
                                        inputs.push(locationField(p, value, [AssetLocationTypes.KeyLocker], [AssetLocationTypes.KeyLocker]));
                                    } else if (p.type == ReportParameterTypes.locationKeyLockerBox) {
                                        inputs.push(locationField(p, value, [AssetLocationTypes.KeyLocker, AssetLocationTypes.KeyLockerBox], [AssetLocationTypes.KeyLockerBox]));
                                    } else if (p.type == ReportParameterTypes.locationUser) {
                                        inputs.push(locationField(p, value, [AssetLocationTypes.Users, AssetLocationTypes.User], [AssetLocationTypes.User]));
                                    } else if (p.type == ReportParameterTypes.datepart) {
                                        inputs.push(
                                            <DropDownField
                                                label={p.alias}
                                                minWidth={150}
                                                selectedItem={{ name: paramValues[p.name], value: paramValues[p.name] } as IDropDownListItem}
                                                onRequestData={async () => {
                                                    const items = enumToSelectOptions(ReportParameterDatePart);

                                                    if (!p.required) {
                                                        items.unshift({ name: 'None', value: '' })
                                                    }

                                                    return items;
                                                }}
                                                onChange={(val) => {
                                                    const obj: Record<string, unknown> = {};

                                                    val = { name: val.name, value: val.value };

                                                    obj[p.name] = val.value;

                                                    setParamValues({
                                                        ...paramValues,
                                                        ...obj
                                                    });
                                                }}>

                                            </DropDownField>
                                        )
                                    }

                                    return <Grid item xs={12} md={4} key={i}>
                                        {inputs.map((inpt, k) => {
                                            return <div key={k}>
                                                <div style={{ padding: 5, marginBottom: 5 }}>{inpt}</div>
                                            </div>;
                                        })}
                                    </Grid>
                                })}
                            </Grid>


                        </div>

                        {errorMessage != null && (
                            <div style={{ color: "red", padding: 10 }}>
                                {errorMessage}
                            </div>
                        )}

                    </div>
                </FloatingInputLabel>
            </div>
        )}





    </Fragment >;
};


export default ReportParamInput;
